// src/pages/landing/DataDeletionPage.js
import React from 'react';
import LandingLayout from '../../components/landing/LandingLayout';
import { useLanguage } from '../../context/LanguageContext';
import { C, TYPE, SP, W } from '../../components/landing/tokens';

const LAST_UPDATED = '27 June 2026';

const DataDeletionPage = () => {
    const { isRTL } = useLanguage();

    // F16 — skin only. The legal prose is deliberate text and is not touched.
    const sectionTitle = { ...TYPE.title, color: C.ink };
    const body = { ...TYPE.body, color: C.inkMuted };
    const legalLink = { color: C.ink, textDecoration: 'none', borderBlockEnd: `1px solid ${C.lineStrong}` };

    return (
        <div style={{ background: C.paper, color: C.ink }} dir={isRTL ? 'rtl' : 'ltr'}>
            <LandingLayout>
                {/* Hero */}
                <section className="py-16 lg:py-24" style={{ background: C.paperDeep }}>
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 style={{ ...TYPE.displayM, color: C.ink, marginBlockEnd: SP.sm }}>
                            Data Deletion
                        </h1>
                        <p style={{ ...TYPE.caption, color: C.inkFaint }}>
                            Last updated: {LAST_UPDATED}
                        </p>
                    </div>
                </section>

                {/* Content */}
                <section className="py-16" style={{ background: C.paper }}>
                    <div className="mx-auto px-4 sm:px-6 lg:px-8 space-y-10" style={{ maxWidth: W.prose }}>

                        <div>
                            <p className="leading-relaxed" style={body}>
                                AQANA FINANCE LTD respects your right to control your data. This page explains how to
                                delete the data Aqana holds about you and your business, including any data retrieved
                                from connected Facebook or Instagram accounts. If you have any questions, contact us at{' '}
                                <a href="mailto:admin@aqana.uk" style={legalLink}>admin@aqana.uk</a>.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>Option 1 — Disconnect a Connected Account</h2>
                            <p className="leading-relaxed" style={body}>
                                To remove the data associated with a connected Facebook or Instagram account, open the
                                Aqana portal and go to <strong>Marketing &rarr; Connections</strong>, then select{' '}
                                <strong>Disconnect</strong> on the relevant account. Disconnecting immediately removes
                                the stored access tokens and connection records for that account from our systems. No
                                further insight data will be retrieved once an account is disconnected.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>Option 2 — Delete Your Entire Account</h2>
                            <p className="leading-relaxed" style={body}>
                                To request deletion of your entire Aqana account and all associated data, email us at{' '}
                                <a href="mailto:admin@aqana.uk" style={legalLink}>admin@aqana.uk</a>{' '}
                                from the email address registered to your account, with the subject line{' '}
                                <strong>"Data Deletion Request"</strong>. We will verify your request and proceed with
                                deletion.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>What Gets Deleted</h2>
                            <ul className="list-disc list-inside space-y-2 leading-relaxed" style={body}>
                                <li>Stored access tokens for connected Facebook and Instagram accounts.</li>
                                <li>Connection records and any cached social insight data.</li>
                                <li>For full account deletion: your account profile, business configuration, and
                                    operational data held in the Service.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>Timeline</h2>
                            <p className="leading-relaxed" style={body}>
                                Disconnecting a connected account takes effect immediately. Full account deletion
                                requests are processed within 30 days of verification, after which the data is
                                permanently removed from our active systems, subject to any retention required by law.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>Contact</h2>
                            <p className="leading-relaxed" style={body}>
                                For help with any deletion request, contact us at{' '}
                                <a href="mailto:admin@aqana.uk" style={legalLink}>admin@aqana.uk</a>.
                            </p>
                        </div>

                    </div>
                </section>
            </LandingLayout>
        </div>
    );
};

export default DataDeletionPage;
